// #tag: DATA FETCHING AND MUTATION REACT HOOKS
// #purpose: Wraps React Query hooks for fetching CMS content, results, admit cards, opportunities, notifications, and student vault documents.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthProvider';
import {
  fetchAdmitCardStatuses,
  fetchDashboardSummary,
  fetchDashboardWidgets,
  fetchExamTimeline,
  fetchExamTimelineByExamId,
  fetchExamTypes,
  fetchNavigation,
  fetchPageSections,
  fetchResultCategories,
  fetchSiteConfig,
  fetchVaultCategories,
} from '../api/cms';
import { fetchVaultDocuments } from '../api/vault';
import { fetchAdmitCards } from '../api/admitCards';
import {
  fetchCalendarEvents,
  fetchNotificationConfig,
  fetchNotificationPreferences,
  fetchNotifications,
  updateNotificationPreferences,
  markNotificationRead,
  markAllNotificationsRead,
  dismissNotification,
} from '../api/notifications';
import { fetchLatestResults, fetchResultById, fetchResults, type ResultsQuery } from '../api/results';
import {
  fetchClosingSoonOpportunities,
  fetchFeaturedOpportunities,
  fetchLatestOpportunities,
  fetchOpportunities,
  fetchOpportunityBySlug,
  fetchOpportunityMeta,
  fetchRecentlyClosedOpportunities,
  fetchTrendingOpportunities,
  type OpportunitiesQuery,
} from '../api/opportunities';
import type { AdmitCardsQuery } from '../api/admitCards';
import type { NotificationPreferences } from '../types';

// #tag: NAVIGATION DATA HOOK
// #purpose: Queries active navigation menu items for header, footer, or floating docks.
export function useNavigation(context = 'primary') {
  return useQuery({ queryKey: ['navigation', context], queryFn: () => fetchNavigation(context) });
}

// #tag: SITE CONFIG DATA HOOK
// #purpose: Retrieves dynamic site branding, contact info, and feature flags.
export function useSiteConfig() {
  return useQuery({ queryKey: ['site-config'], queryFn: fetchSiteConfig });
}

// #tag: PAGE SECTIONS DATA HOOK
// #purpose: Queries CMS layout sections and structured components for a specific page.
export function usePageSections(page: string) {
  return useQuery({ queryKey: ['page-sections', page], queryFn: () => fetchPageSections(page) });
}

export function useExamTypes() {
  return useQuery({ queryKey: ['exam-types'], queryFn: fetchExamTypes });
}

export function useResultCategories() {
  return useQuery({ queryKey: ['result-categories'], queryFn: fetchResultCategories });
}

export function useAdmitCardStatuses() {
  return useQuery({ queryKey: ['admit-card-statuses'], queryFn: fetchAdmitCardStatuses });
}

export function useVaultCategories() {
  return useQuery({ queryKey: ['vault-categories'], queryFn: fetchVaultCategories });
}

// #tag: VAULT DOCUMENTS QUERY HOOK
// #purpose: Queries the authenticated student's vault documents with search and category filters.
export function useVaultDocuments(params?: { q?: string; categoryId?: string }) {
  return useQuery({
    queryKey: ['vault-documents', params],
    queryFn: () => fetchVaultDocuments(params),
    keepPreviousData: true,
  });
}

export function useDashboardWidgets() {
  return useQuery({ queryKey: ['dashboard-widgets'], queryFn: fetchDashboardWidgets });
}

// #tag: DASHBOARD SUMMARY METRICS HOOK
// #purpose: Fetches aggregated counts for saved exams, unread notifications, upcoming events, and vault documents.
export function useDashboardSummary() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: fetchDashboardSummary,
    enabled: isAuthenticated,
  });
}

export function useExamTimeline(id: string | undefined) {
  return useQuery({
    queryKey: ['exam-timeline', id],
    queryFn: () => fetchExamTimeline(id!),
    enabled: !!id,
  });
}

export function useExamTimelineByExamId(examId: string | undefined) {
  return useQuery({
    queryKey: ['exam-timeline-by-exam', examId],
    queryFn: () => fetchExamTimelineByExamId(examId!),
    enabled: !!examId,
    retry: false,
  });
}

// #tag: RESULTS QUERY HOOK
// #purpose: Queries paginated exam scorecards and result listings matching search criteria.
export function useResults(query: ResultsQuery) {
  return useQuery({ queryKey: ['results', query], queryFn: () => fetchResults(query) });
}

export function useLatestResults(limit = 3) {
  return useQuery({ queryKey: ['results', 'latest', limit], queryFn: () => fetchLatestResults(limit) });
}

export function useResult(id: string | undefined) {
  return useQuery({
    queryKey: ['results', id],
    queryFn: () => fetchResultById(id!),
    enabled: !!id,
  });
}

// #tag: ADMIT CARDS QUERY HOOK
// #purpose: Queries hall ticket releases and examination dates with filter parameters.
export function useAdmitCards(query: AdmitCardsQuery) {
  return useQuery({ queryKey: ['admit-cards', query], queryFn: () => fetchAdmitCards(query) });
}

// #tag: OPPORTUNITIES QUERY HOOK
// #purpose: Queries career recruitments and government job openings with pagination and urgency filters.
export function useOpportunities(query: OpportunitiesQuery) {
  return useQuery({ queryKey: ['opportunities', query], queryFn: () => fetchOpportunities(query) });
}

export function useLatestOpportunities(limit = 6) {
  return useQuery({ queryKey: ['opportunities', 'latest', limit], queryFn: () => fetchLatestOpportunities(limit) });
}

export function useFeaturedOpportunities(limit = 4) {
  return useQuery({ queryKey: ['opportunities', 'featured', limit], queryFn: () => fetchFeaturedOpportunities(limit) });
}

export function useTrendingOpportunities(limit = 6) {
  return useQuery({ queryKey: ['opportunities', 'trending', limit], queryFn: () => fetchTrendingOpportunities(limit) });
}

export function useClosingSoonOpportunities(limit = 6) {
  return useQuery({ queryKey: ['opportunities', 'closing-soon', limit], queryFn: () => fetchClosingSoonOpportunities(limit) });
}

export function useRecentlyClosedOpportunities(limit = 6) {
  return useQuery({ queryKey: ['opportunities', 'recently-closed', limit], queryFn: () => fetchRecentlyClosedOpportunities(limit) });
}

export function useOpportunityMeta() {
  return useQuery({ queryKey: ['opportunities', 'meta'], queryFn: fetchOpportunityMeta });
}

export function useOpportunity(slug: string | undefined) {
  return useQuery({
    queryKey: ['opportunities', slug],
    queryFn: () => fetchOpportunityBySlug(slug!),
    enabled: !!slug,
  });
}

// #tag: USER NOTIFICATIONS INBOX HOOK
// #purpose: Queries personalized alert feed based on followed exams and notification category preferences.
export function useNotifications(params?: { categoryId?: string; limit?: number }) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => fetchNotifications(params),
    enabled: isAuthenticated,
  });
}

export function useNotificationConfig() {
  return useQuery({ queryKey: ['notification-config'], queryFn: fetchNotificationConfig });
}

export function useNotificationPreferences() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchNotificationPreferences,
    enabled: isAuthenticated,
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: Partial<NotificationPreferences>) => updateNotificationPreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useDismissNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dismissNotification(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// #tag: CALENDAR EVENTS QUERY HOOK
// #purpose: Retrieves scheduled milestones and exam dates for calendar display.
export function useCalendarEvents(params?: {
  limit?: number;
  examIds?: string[];
  upcoming?: boolean;
}) {
  return useQuery({
    queryKey: ['calendar', params],
    queryFn: () => fetchCalendarEvents(params),
  });
}
